import * as React from "react";
import { useMemo, useState } from "react";
import { Grid, Header, Loader, Pagination } from "semantic-ui-react";
import { RouteComponentProps, withRouter } from "react-router";
import { useIntl } from "react-intl";
import { keyBy } from "lodash";

import { PlayersServices } from "@/services";
import * as DTO from "../../../interfaces";
import { SocialNetworkPostStatus } from "@/interfaces";
import { fullNameFrom, useSegmentation, useSocialCoachSession } from "@/utils";

import { KeywordSet } from "./KeywordSet";

import { ComplianceType, descriptors } from "./descriptors";
import { ComplianceContainer } from "./styled";
import "./styles.scss";
import { ComplianceExportModal } from "./ComplianceExportModal";
import { PageWrapper } from "@/components/PageWrapper";
import { usePostSearch } from "@/utils/usePostSearch";
import { PostFeed } from "@/containers/Coach/Compliance/PostFeed";
import { PostSearchQuery } from "@/services/bedrock";
import { ComplianceFilters } from "@/containers/Coach/Compliance/ComplianceFilters";

type Props = RouteComponentProps;

const ComplianceFC: React.FC<Props> = () => {
  const { formatMessage } = useIntl();

  const { coachId } = useSocialCoachSession();

  const [players, setPlayers] = React.useState<{ [key: string]: DTO.SubscribedPlayerSearchResponse }>({});
  const { segmentationOptions } = useSegmentation(["REGION", "BRANCH", "COST_CENTER", "STATE"]);

  const { searchPosts } = usePostSearch();
  const [searchQuery, setSearchQuery] = useState<PostSearchQuery>({
    page: 1,
    perPage: 10,
    statuses: [SocialNetworkPostStatus.POSTED],
  });
  const { posts, isFetching } = searchPosts(searchQuery);
  const totalPages = useMemo(() => posts?.pagination?.totalPages || 1, [posts?.pagination?.totalPages]);

  const [showExportModal, setShowExportModal] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (coachId === undefined) return;
    PlayersServices.searchSubscribedAllPlayersByTag(coachId).then(response => {
      setPlayers(keyBy(response.content, "playerAccount.playerId"));
    });
  }, [coachId]);

  const playerOptions = useMemo(() => {
    return Object.values(players).map(player => {
      return {
        key: player.playerAccount.playerId,
        text: fullNameFrom(player.playerAccount),
        value: player.playerAccount.playerId,
      };
    });
  }, [players]);

  return (
    <PageWrapper title={formatMessage({ ...descriptors[ComplianceType.title] })} hideSeparator={true}>
      <Grid columns={1} className={"complianceGrid"}>
        <ComplianceContainer columns={2}>
          <Grid.Column className={"flaggedKeywordColumn"}>
            <KeywordSet policy={"FLAG"} />
          </Grid.Column>
          <Grid.Column className={"blockedKeywordColumn"}>
            <KeywordSet policy={"BLOCK"} />
          </Grid.Column>
        </ComplianceContainer>
        <Grid.Row>
          <div className={"greyBox postFeed"}>
            <Header as="h3">
              Post Feed
              {isFetching && <Loader active inline size="small" style={{ marginLeft: "8px" }} />}
            </Header>
            <ComplianceFilters
              query={searchQuery}
              playerOptions={playerOptions}
              segmentationOptions={segmentationOptions}
              onUpdate={newQuery => {
                setSearchQuery({
                  ...newQuery,
                  page: 1,
                });
              }}
              onExport={() => setShowExportModal(true)}
            />
            <PostFeed postResponse={posts} players={players} />
            <div className={"paginationContainer"}>
              <Pagination
                activePage={searchQuery.page}
                onPageChange={(_, { activePage }) => {
                  setSearchQuery(q => {
                    return {
                      ...q,
                      page: activePage as number,
                    };
                  });
                  scrollTo(0, 0);
                }}
                totalPages={totalPages}
                disabled={isFetching}
                pointing
                secondary
              />
            </div>
          </div>
        </Grid.Row>
        <ComplianceExportModal
          open={showExportModal}
          onClose={() => setShowExportModal(false)}
          players={players}
          playerOptions={playerOptions}
          segmentationOptions={segmentationOptions}
        />
      </Grid>
    </PageWrapper>
  );
};

export const CompliancePage = withRouter(ComplianceFC);
